import { FormEvent, useEffect, useState } from 'react';
import { UsersApi } from '../../api/resources';
import { ApiError } from '../../api/client';
import type { User } from '../../api/types';
import { useToast } from '../../context/ToastContext';
import { formatDateTime } from '../../utils/format';
import { useAuth } from '../../context/AuthContext';
import { NavLink } from 'react-router-dom';

const emptyForm = { username: '', email: '', fullName: '', role: 'EMPLEADO' };

export function Users() {
  const { notify } = useToast();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [lastTempPassword, setLastTempPassword] = useState<{ username: string; password: string } | null>(null);
  const [quota, setQuota] = useState<{ maxUsers: number; activeUsers: number } | null>(null);
  const quotaFull = quota !== null && quota.activeUsers >= quota.maxUsers;

  async function load() {
    setLoading(true);
    try {
      const [result, q] = await Promise.all([UsersApi.list({ search: search || undefined }), UsersApi.quota()]);
      setUsers(result.users);
      setQuota(q);
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible cargar los usuarios.', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const { user } = await UsersApi.create(form);
      setLastTempPassword({ username: user.username, password: user.temporaryPassword });
      notify('Usuario creado correctamente.', 'success');
      setForm(emptyForm);
      setShowForm(false);
      load();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible crear el usuario.', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(u: User) {
    try {
      if (u.isActive) {
        if (!window.confirm(`¿Desactivar al usuario "${u.fullName}"?`)) return;
        await UsersApi.deactivate(u.id);
      } else {
        await UsersApi.activate(u.id);
      }
      notify('Estado actualizado.', 'success');
      load();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible actualizar el estado.', 'error');
    }
  }

  async function resetPassword(u: User) {
    if (!window.confirm(`¿Restablecer la contraseña de "${u.fullName}"? Se generara una contraseña temporal.`)) return;
    try {
      const { temporaryPassword } = await UsersApi.resetPassword(u.id);
      setLastTempPassword({ username: u.username, password: temporaryPassword });
      notify('Contraseña restablecida.', 'success');
      load();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible restablecer la contraseña.', 'error');
    }
  }

  return (
    <div>
      <div className="flex-between">
        <h1>Usuarios</h1>
        <button type="button" className="btn btn-primary" onClick={() => setShowForm((v) => !v)} disabled={quotaFull && !showForm}>
          {showForm ? 'Cerrar' : '+ Nuevo usuario'}
        </button>
      </div>

      {quota && (
        <div className={`alert ${quotaFull ? 'alert-warning' : 'alert-info'}`} role="status">
          Usuarios activos: <strong>{quota.activeUsers} de {quota.maxUsers}</strong>.
          {quotaFull && ' Para habilitar más usuarios, solicítalo a la plataforma.'}
        </div>
      )}

      {lastTempPassword && (
        <div className="alert alert-info" role="status">
          Contraseña temporal para <strong>{lastTempPassword.username}</strong>: <code>{lastTempPassword.password}</code>. Comunicasela de forma
          segura; debera cambiarla al iniciar sesion.{' '}
          <button type="button" className="btn-link" onClick={() => setLastTempPassword(null)}>
            Cerrar aviso
          </button>
        </div>
      )}

      {showForm && (
        <div className="card">
          <h2>Nuevo usuario</h2>
          <form onSubmit={handleSubmit} noValidate>
            <div className="form-grid">
              <div className="field">
                <label htmlFor="uusername">Usuario</label>
                <input id="uusername" required value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="ufullname">Nombre completo</label>
                <input id="ufullname" required value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="uemail">Correo electronico</label>
                <input id="uemail" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="urole">Rol</label>
                <select id="urole" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  <option value="EMPLEADO">Empleado</option>
                  <option value="ADMIN">Administrador</option>
                </select>
              </div>
            </div>
            <span className="hint">Se generara una contraseña temporal automaticamente; el usuario debera cambiarla al ingresar.</span>
            <div className="actions-row mt-1">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Creando...' : 'Crear usuario'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <div className="field" style={{ maxWidth: 320 }}>
          <label htmlFor="usearch">Buscar</label>
          <input id="usearch" type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nombre, usuario o correo..." />
        </div>

        {loading ? (
          <div className="loading-block">Cargando usuarios...</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Nombre</th>
                  <th>Correo</th>
                  <th>Rol</th>
                  <th>Estado</th>
                  <th>Ultimo ingreso</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.username}</td>
                    <td>{u.fullName}</td>
                    <td>{u.email}</td>
                    <td>{u.role === 'ADMIN' ? 'Administrador' : 'Empleado'}</td>
                    <td>
                      <span className={`pill ${u.isActive ? 'pill-active' : 'pill-inactive'}`}>{u.isActive ? 'Activo' : 'Inactivo'}</span>
                    </td>
                    <td>{u.lastLoginAt ? formatDateTime(u.lastLoginAt) : 'Nunca'}</td>
                    <td>
                      <div className="actions-row">
                        {u.role === 'ADMIN' ? (
                          u.id === currentUser?.id && (
                            <NavLink to="/perfil" className="btn btn-secondary btn-sm">
                              Cambiar clave
                            </NavLink>
                          )
                        ) : (
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => resetPassword(u)}>
                            Restablecer clave
                          </button>
                        )}
                        {u.id !== currentUser?.id && (
                          <button type="button" className={`btn btn-sm ${u.isActive ? 'btn-danger' : 'btn-secondary'}`} onClick={() => toggleActive(u)}>
                            {u.isActive ? 'Desactivar' : 'Activar'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
